import React, { useRef, useCallback, useMemo, useEffect, useState, Fragment, useContext } from "react";
import { useSearchParams } from "react-router-dom";

import COMMON from "common";
import api from "services/api";
import sanitizeObject from "common/sanitize-object";
import serveRequestErrors from "common/serve-request-errors";
import AppEmptyState from "components/app-empty-state";
import AppTable, { AppTableCell } from "components/app-table";
import pdfIcon from "assets/images/pdf-icon.svg";
import filterIcon from "assets/images/components/app-multi-select-input/filter-icon.svg";
import { AxiosContext } from "contexts/with-interceptor-provider";
import { CALENDAR_FORMAT, formatDate, formatDatePattern, isDateBeforeToday, isEndDateBeforeStartDate } from "common/calendar";
import placeholderFormat from "common/placeholder-format";
import AppSearchInput from "components/app-search-input";
import AppInput from "components/app-input";
import AppSelectInput from "components/app-select-input";
import AppButton from "components/app-button";
import { useFormik } from "formik";
import queryParamsEntries from "common/query-params-entries";
import AppCalendarInput from "components/app-calendar-input";
import * as yup from "yup";
import ERRORS from "common/errors";
import AppStatus from "components/app-status";

const PageCertificates = (props) => {
    const searchInputRef = useRef();
    const context = useContext(AxiosContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const memoSetSearchParams = useRef(setSearchParams);
    const paramsRef = useRef({ page: parseInt(searchParams.get("page")) || 1, sort: searchParams.get("sort") || "" });
    const [data, setData] = useState({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });
    const [loading, setLoading] = useState(true);
    const isEmptyState = useMemo(() => !loading && data.items.length === 0, [loading, data]);
    const calendarDisplayFormat = CALENDAR_FORMAT.DATE_FORMAT + " " + CALENDAR_FORMAT.MONTH_FORMAT + " " + CALENDAR_FORMAT.YEAR_FORMAT;
    const initialValues = useMemo(() => ({ assignee: "", title: "", certificateStatus: "", expiryStartDate: "", expiryEndDate: "" }), []);
    const formik = useFormik({
        initialValues,
        validationSchema: yup.object({
            expiryStartDate: yup.string().test("expiryStartDate", ERRORS.START_DATE, function (value) {
                if (!value || !this.parent.expiryEndDate)
                    return true;

                return isDateBeforeToday(value, this.parent.expiryEndDate);
            }),
            expiryEndDate: yup.string().test("expiryEndDate", ERRORS.END_DATE, function (value) {
                if (!value || !this.parent.expiryStartDate)
                    return true;

                return isDateBeforeToday(this.parent.expiryStartDate || new Date(), value);
            }),
        }),
        onSubmit: (values) => {
            onHandleSubmitSearch(values);
        },
    });

    //prettier-ignore
    const onHandleExpiryStartDate = useCallback((name, value) => {
        formik.setFieldValue(name, value);

        if (isEndDateBeforeStartDate(value, formik.values.expiryEndDate)) formik.setFieldValue("expiryEndDate", "");
    }, [formik]);


    //prettier-ignore
    const onHandleSubmitSearch = (values) => {
        searchInputRef.current.onhandleCloseAdvanceSearch();

        paramsRef.current = { ...paramsRef.current, page: 1, ...values };

        onHandleGetList();
    };

    //prettier-ignore
    const onHandleResetSearch = () => {
        formik.setValues(formik.initialValues);

        paramsRef.current = { page: 1, sort: "", ...formik.initialValues };

        onHandleGetList();
    };

    const onHandleGetList = useCallback(async () => {
        let response = null;

        setLoading(true);

        setData({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });

        try {
            const payload = { ...paramsRef.current, size: 10 };

            memoSetSearchParams.current(sanitizeObject(payload), { replace: true });

            payload.page = paramsRef.current.page - 1;

            if (payload.expiryStartDate) {
                payload.expiryStartDate = formatDatePattern(new Date(payload.expiryStartDate), CALENDAR_FORMAT.CALENDAR_FORMAT);
            }

            if (payload.expiryEndDate) {
                payload.expiryEndDate = formatDatePattern(new Date(payload.expiryEndDate), CALENDAR_FORMAT.CALENDAR_FORMAT);
            }

            response = await api.get.certificate.list(sanitizeObject(payload));
        } catch (error) {
            const wasCancelled = serveRequestErrors(error);
            if (wasCancelled) return;
        }

        if (response) {
            setData((prev) => ({
                ...prev,
                page: paramsRef.current.page,
                prev: !response.first,
                next: !response.last,
                items: response.content,
                total: response.totalPages,
                totalElements: response.totalElements,
                offset: response?.pageable?.offset || 0,
                status: response?.pageable,
            }));
        }

        setLoading(false);
    }, []);

    //prettier-ignore
    const onHandlePagination = useCallback((event) => {
        const control = event.currentTarget?.getAttribute("data-ctrl");

        if (control) {
            if (control === "prev") {
                if (paramsRef.current.page <= 1) return;
                paramsRef.current.page -= 1;
            } else {
                if (paramsRef.current.page >= data.total) return;
                paramsRef.current.page += 1;
            }
        } else {
            paramsRef.current.page = event.target.value;
        }

        onHandleGetList();
    }, [onHandleGetList, data.total]);

    //prettier-ignore
    const onHandleSort = useCallback((id, order) => {
        paramsRef.current.sort = order ? id + "," + order : "";

        paramsRef.current.page = 1;

        onHandleGetList();
    }, [onHandleGetList]);

    //prettier-ignore
    const StatusCell = useCallback(({ row }) => {
        const status = row.original?.status;
        return (<AppStatus status={status ? status : "-"} />);
    }, []);

    //prettier-ignore
    const ExpiryDateCell = useCallback(({ row }) => {
        const expiryDate = row.original?.expiryDate;
        return <AppTableCell center value={expiryDate ? formatDate(expiryDate) : "-"} />;
    }, [])

    //prettier-ignore
    const DaysLeftCell = useCallback(({ row }) => {
        const daysLeft = row.original?.daysLeft;
        return <AppTableCell center value={daysLeft ? daysLeft : "-"} />;
    }, [])

    //prettier-ignore
    const CreatedDateCell = useCallback(({ row }) => {
        const createdDate = row.original?.createdDate;
        return <AppTableCell center value={createdDate ? formatDate(createdDate) : "-"} />;
    }, [])

    //prettier-ignore
    const NameCell = useCallback(({ row }) => {
        return (
            <div className="name-column">
                <AppTableCell left value={row.original.employeeName} />
            </div>)
    }, []);

    //prettier-ignore
    const AttachmentCell = useCallback(({ row }) => {
        const filePath = row.original?.filePath;
        const value = filePath ? (<a href={filePath} target="_blank" rel="noopener noreferrer"><img className="table__file" src={pdfIcon} alt="attachment" /></a>) : ("-");
        return (
            <AppTableCell center value={value} />
        );
    }, []);

    //prettier-ignore
    const columns = useMemo(() => [
        {
            Header: "Assignee",
            accessor: "employeeName",
            disableSortBy: false,
            Cell: NameCell,
        },
        {
            Header: "Course",
            accessor: "courseTitle",
            disableSortBy: false,
        },
        {
            Header: "Certificate Status",
            accessor: "status",
            disableSortBy: false,
            Cell: StatusCell,
        },
        {
            Header: "Attachment",
            accessor: "attachment",
            disableSortBy: true,
            Cell: AttachmentCell,
        },
        {
            Header: "Expiry Date",
            accessor: "expiryDate",
            disableSortBy: false,
            Cell: ExpiryDateCell,
        },
        {
            Header: "Days Left",
            accessor: "daysLeft",
            disableSortBy: true,
            Cell: DaysLeftCell,
        },
        {
            Header: "Uploaded Date",
            accessor: "createdDate",
            disableSortBy: false,
            Cell: CreatedDateCell,
        }
    ], [NameCell, StatusCell, AttachmentCell, ExpiryDateCell, DaysLeftCell, CreatedDateCell]);

    //prettier-ignore
    const advanceSearchValues = useMemo(() => {
        const params = queryParamsEntries(searchParams);
        let values = {
            assignee: params.assignee,
            title: params.title,
            certificateStatus: params.certificateStatus,
            expiryStartDate: params.expiryStartDate,
            expiryEndDate: params.expiryEndDate
        };

        return values;
    }, [searchParams]);

    //prettier-ignore
    const onHandleRemoveField = (field) => {
        formik.setFieldValue(field, "");
        paramsRef.current = { ...paramsRef.current, page: 1, [field]: "" };
        onHandleGetList();
    };

    //prettier-ignore
    const onHandleShowAdvanceSearch = (event) => {
        if (searchInputRef.current?.onHandleShowAdvanceSearch) {
            searchInputRef.current.onHandleShowAdvanceSearch(event);
        }
    };

    //prettier-ignore
    const headerButtons = useMemo(() => {
        const buttons = [
            { label: "Filter", icon: filterIcon, outline: true, onClick: onHandleShowAdvanceSearch },
        ];
        return buttons;
    }, []);

    useEffect(() => {
        onHandleGetList();
    }, [onHandleGetList]);

    useEffect(() => {
        return () => {
            context.onHandleCancelRequest(COMMON.ENDPOINT_PATH.CERTIFICATE.LIST);
        };
    }, [context]);

    return (
        <div className="page-certificates">
            <div className="certificates">
                <AppSearchInput ref={searchInputRef} multiValues={advanceSearchValues} onRemoveField={onHandleRemoveField} buttons={headerButtons}>
                    <form className="app-advance-search-form" onSubmit={formik.handleSubmit}>
                        <div className="advance-form">
                            <div className="advance-form__inputs">
                                <AppInput type="text" name="assignee" label="Assignee Name" placeholder="Enter assignee name" value={formik.values.assignee} onChange={formik.handleChange} />

                                <AppInput type="text" name="title" label="Course Title" placeholder="Enter course title" value={formik.values.title} onChange={formik.handleChange} />

                                <AppSelectInput name="certificateStatus" label="Certificate Status" placeholder="Select Certificate Status" options={COMMON.DROPDOWNS.CERTIFICATE} value={formik.values.certificateStatus} onChange={formik.handleChange} />
                            </div>
                            <div className="advance-form__inputs advance-form__course_certificate_search">
                                <AppCalendarInput name="expiryStartDate" label="Expiry Date From" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} value={formik.values.expiryStartDate} error={formik.errors.expiryStartDate} touched={formik.touched.expiryStartDate} onChange={onHandleExpiryStartDate} />

                                <AppCalendarInput name="expiryEndDate" label="Expiry Date To" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} minDate={formik.values.expiryStartDate} value={formik.values.expiryEndDate} error={formik.errors.expiryEndDate} touched={formik.touched.expiryEndDate} onChange={formik.setFieldValue} />

                            </div>

                            <div className="advance-form__button-container">
                                <AppButton type="button" label="Clear" outline onClick={onHandleResetSearch} />
                                <AppButton type="submit" label="Search" />
                            </div>
                        </div>
                    </form>
                </AppSearchInput>
                {isEmptyState && <AppEmptyState title="No records found" description="You don’t have any records yet" disabledButton={true} />}

                {!isEmptyState && (
                    <Fragment>
                        <AppTable columns={columns} pages={data} onHandlePagination={onHandlePagination} onHandleSort={onHandleSort} />
                    </Fragment>
                )}
            </div>
        </div>
    );
};

export default PageCertificates;
